import React, { useMemo, useState } from "react";

import DCDialLogoBig from "icons/DCDialLogoBig";
import Button from "components/form/Button";
import MessageToast from "components/ui-component/MessageToast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useUserData } from "store/slices/authSlice";
import { useIsLogin } from "store/slices/commonSlice";
import { clearSlice } from "utils/HelperFunctions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import Helment from "components/layout/helmet";
import { TOKEN_STORAGE_KEY } from "store/api/axiosApi";
import { decode, encode } from "utils/HelperFunctions";
import {
  getReturnClusterSlug,
  readInvalidReason,
  readRememberedClusterId,
} from "utils/invalidRoute";
import { isAllowedClusterId } from "utils/clusterAccess";
import { getCustomDomainCluster } from "config/customDomainClusters";
import { RootState } from "store/store";
import { INVALID_PAGE_CONTENT } from "constants/invalidPageContent";

const NO_PORTAL_MESSAGE =
  "We couldn't find your customer portal. Please contact the administrator for help.";

const NotFound = () => {
  const navigate = useNavigate();
  const userData = useUserData();
  const dispatch = useAppDispatch();
  const isLogin = useIsLogin();
  const location = useLocation();
  const reason = readInvalidReason(location.state);
  const content = INVALID_PAGE_CONTENT[reason];
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const { clusterId: routeClusterSlug } = useParams();
  const lastClusterId = useAppSelector(
    (state: RootState) => state.dashboard.portalSettingsClusterId
  );
  const accountClusterId = userData?.data?.accountData?.clusterid;

  const clusterSlug = useMemo(
    () =>
      getReturnClusterSlug({
        customDomainSlug: getCustomDomainCluster(window.location.hostname)
          ?.clusterSlug,
        routeClusterSlug,
        accountClusterId,
        lastClusterId: lastClusterId || readRememberedClusterId(),
        decodeClusterSlug: (slug) => {
          try {
            return decode(slug, true);
          } catch {
            return null;
          }
        },
        encodeClusterId: (id) => encode(id, "ci_"),
        isValidClusterId: isAllowedClusterId,
      }),
    [routeClusterSlug, accountClusterId, lastClusterId]
  );

  return (
    <>
      <Helment />
      <div className="portal-shell h-[var(--shell-vh)] w-[var(--shell-vw)] bg-bgPrimary flex items-center justify-center">
        <div className="max-w-[684px] flex flex-col gap-[60px] p-5">
          <div className="flex flex-col items-center">
            <div className="lg:w-fit w-36 flex">
              <DCDialLogoBig />
            </div>
            <div className="lg:text-[80px] text-[60px] font-black text-txtSecondary pt-[60px]">
              404
            </div>
            <div className="lg:text- text-2xl font-bold text-iconObscure pt-[30px] text-center">
              {content.title}
            </div>
            <div className="pt-[30px] text-center text-txtParagraph lg:text-[26px] text-xl leading-normal lg:pt-4">
              {content.description}
            </div>
          </div>
          <Button
            text="Return to customer portal"
            onClick={() => {
              // a turned-off portal would only send the visitor straight back here
              if (!clusterSlug || reason === "unavailable") {
                setToastMessage(NO_PORTAL_MESSAGE);
                return;
              }

              if (!isLogin) {
                clearSlice(dispatch);
                localStorage.removeItem(TOKEN_STORAGE_KEY);
                navigate(`/${clusterSlug}/login`, { replace: true });
              } else {
                navigate(`/${clusterSlug}/dashboard`);
              }
            }}
          />
        </div>
      </div>
      <MessageToast
        variant="error"
        content={toastMessage}
        width="w-[calc(100%-32px)] max-w-[402px]"
        className="!right-auto !left-1/2 -translate-x-1/2"
        onCloseClick={() => setToastMessage(null)}
      />
    </>
  );
};

export default NotFound;
